import React, { useState, useEffect } from "react";
import { Form, Input, Button, Select, DatePicker, Card, Divider, Typography, Space, Upload, message, Row, Col } from "antd";
import { PlusOutlined, InboxOutlined, GlobalOutlined, EnvironmentOutlined, UserOutlined, FileTextOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Dragger } = Upload;
const { Option } = Select;

// 根据事务类型获取表单配置
const getFormConfig = (transactionType) => {
    const configs = {
        PERMIT: {
            title: "申请飞行许可",
            subTitle: "创建飞行许可申请并发送给相关部门",
            recipientOptions: [
                { label: "中国民航局", value: "中国民航局" },
                { label: "北京空管局", value: "北京空管局" },
                { label: "上海空管局", value: "上海空管局" },
                { label: "广州空管局", value: "广州空管局" },
            ],
            defaultSubject: "申请飞行许可",
            attachments: true,
            fields: [
                {
                    name: "permitType",
                    label: "许可类型",
                    type: "select",
                    required: true,
                    options: [
                        { value: "overflight", label: "飞越许可" },
                        { value: "landing", label: "着陆许可" },
                        { value: "technical", label: "技术停留许可" },
                    ],
                },
                {
                    name: "validityPeriod",
                    label: "有效期",
                    type: "daterange",
                    required: true,
                },
            ],
        },
        FUEL: {
            title: "申请加油服务",
            subTitle: "创建燃油申请并发送给供应商",
            recipientOptions: [
                { label: "中航油北京分公司", value: "中航油北京分公司" },
                { label: "中航油上海分公司", value: "中航油上海分公司" },
                { label: "中航油广州分公司", value: "中航油广州分公司" },
            ],
            defaultSubject: "申请加油服务",
            attachments: true,
            fields: [
                {
                    name: "fuelAmount",
                    label: "燃油量 (KG)",
                    type: "input",
                    required: true,
                },
                {
                    name: "fuelProvider",
                    label: "燃油供应商",
                    type: "select",
                    required: true,
                    options: [
                        { value: "CNAF", label: "CNAF" },
                        { value: "BP", label: "BP" },
                        { value: "Shell", label: "Shell" },
                    ],
                },
            ],
        },
        SLOT: {
            title: "申请起降时刻",
            subTitle: "创建起降时刻申请并发送给机场管理部门",
            recipientOptions: [
                { label: "首都机场管理局", value: "首都机场管理局" },
                { label: "浦东机场管理局", value: "浦东机场管理局" },
                { label: "白云机场管理局", value: "白云机场管理局" },
            ],
            defaultSubject: "申请起降时刻",
            attachments: true,
            fields: [
                {
                    name: "slotDate",
                    label: "时刻日期",
                    type: "datepicker",
                    required: true,
                },
                {
                    name: "slotTime",
                    label: "申请时段",
                    type: "timepicker",
                    required: true,
                },
            ],
        },
        HANDLING: {
            title: "申请地面服务",
            subTitle: "创建地面服务申请并发送给服务提供商",
            recipientOptions: [
                { label: "首都机场地服公司", value: "首都机场地服公司" },
                { label: "浦东机场地服公司", value: "浦东机场地服公司" },
                { label: "白云机场地服公司", value: "白云机场地服公司" },
            ],
            defaultSubject: "申请地面服务",
            attachments: true,
            fields: [
                {
                    name: "handlingServices",
                    label: "服务类型",
                    type: "checkbox",
                    required: true,
                    options: [
                        { value: "pushback", label: "推出服务" },
                        { value: "towing", label: "牵引服务" },
                        { value: "cleaning", label: "清洁服务" },
                        { value: "water", label: "供水服务" },
                    ],
                },
            ],
        },
        NEW: {
            title: "新建事务",
            subTitle: "创建新的事务请求",
            recipientOptions: [
                { label: "中国民航局", value: "中国民航局" },
                { label: "首都机场管理局", value: "首都机场管理局" },
                { label: "中航油北京分公司", value: "中航油北京分公司" },
                { label: "首都机场地服公司", value: "首都机场地服公司" },
            ],
            defaultSubject: "新建事务",
            attachments: true,
            fields: [],
        },
    };

    return configs[transactionType] || configs.NEW;
};

// 模拟邮件编辑器和不同类型事务的表单
const TransactionActionForm = ({ initialValues, onCancel, onSubmit }) => {
    const [form] = Form.useForm();
    const [recipientOptions, setRecipientOptions] = useState([]);
    const [transactionType, setTransactionType] = useState(initialValues?.transactionType || "NEW");
    const flightInfo = initialValues?.flightInfo || {};
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const formConfig = getFormConfig(transactionType);

    // 当初始值变更时重置表单
    useEffect(() => {
        if (initialValues) {
            form.resetFields();
            setTransactionType(initialValues.transactionType || "NEW");

            // 预填邮件主题和内容，基于事务类型和航班信息
            const emailSubject = initialValues.subject || getEmailSubjectByType(initialValues.transactionType, flightInfo);
            const emailContent = getEmailContentByType(initialValues.transactionType, flightInfo);

            // 获取收件人选项
            const recipients = getMockRecipients(initialValues.transactionType);
            setRecipientOptions(recipients);

            // 设置表单初始值
            form.setFieldsValue({
                subject: emailSubject,
                message: emailContent,
                recipients: recipients.length > 0 ? [recipients[0].value] : [],
                action_date: dayjs(),
                status: "PENDING",
            });
        }
    }, [initialValues, form, flightInfo]);

    // 处理表单提交
    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            // 处理上传的文件
            const formData = {
                ...values,
                related_files: fileList.map((file) => ({
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    uid: file.uid,
                })),
                flightInfo: initialValues.flightInfo,
                transaction_type: transactionType,
            };

            console.log("Form submission data:", formData);

            // 模拟API调用延迟
            await new Promise((resolve) => setTimeout(resolve, 800));

            // 调用父组件的提交方法
            if (onSubmit && typeof onSubmit === "function") {
                onSubmit(formData);
            }
        } catch (err) {
            console.error("Form submission error:", err);
            message.error("发起事务失败，请重试");
        } finally {
            setLoading(false);
        }
    };

    // 根据事务类型获取邮件主题
    const getEmailSubjectByType = (type, flight) => {
        const flightNumber = flight?.flight_number || "未知航班";
        const route = `${flight?.departure_airport || "未知"}-${flight?.arrival_airport || "未知"}`;
        const flightDate = flight?.flight_date ? dayjs(flight.flight_date).format("DDMMMYY") : "未知日期";

        switch (type) {
            case "FUEL":
                return `燃油申请: ${flightNumber} ${route} ${flightDate}`;
            case "PERMIT":
                return `许可申请: ${flightNumber} ${route} ${flightDate}`;
            case "SLOT":
                return `时刻申请: ${flightNumber} ${route} ${flightDate}`;
            case "HANDLING":
                return `地面服务: ${flightNumber} ${route} ${flightDate}`;
            default:
                return `请求: ${flightNumber} ${route} ${flightDate}`;
        }
    };

    // 根据事务类型获取邮件内容模板
    const getEmailContentByType = (type, flight) => {
        const flightDetails = `
航班: ${flight?.flight_number || "N/A"}
日期: ${flight?.flight_date ? dayjs(flight.flight_date).format("YYYY-MM-DD") : "N/A"}
航线: ${flight?.departure_airport || "N/A"} - ${flight?.arrival_airport || "N/A"}
机型: ${flight?.aircraft_type || "TBA"}
注册号: ${flight?.aircraft_reg || "TBA"}
计划起飞: ${flight?.std ? dayjs(flight.std).format("HH:mm") : "N/A"}Z
计划到达: ${flight?.sta ? dayjs(flight.sta).format("HH:mm") : "N/A"}Z
`;

        switch (type) {
            case "FUEL":
                return `尊敬的燃油供应商,

请为以下航班安排加油服务:
${flightDetails}
所需燃油量: 待定

谢谢您的配合。

此致,
运行团队`;

            case "PERMIT":
                return `尊敬的航空当局,

我们希望为以下航班申请飞越/着陆许可:
${flightDetails}
运营人: FlyMeta Airlines
航空器注册号: ${flight?.aircraft_reg || "TBA"}

请告知进一步要求。

此致,
运行团队`;

            case "SLOT":
                return `尊敬的时刻协调部门,

我们希望为以下航班申请起降时刻:
${flightDetails}
请协助安排。

此致,
运行团队`;

            case "HANDLING":
                return `尊敬的地面服务提供商,

请为以下航班安排地面服务:
${flightDetails}
服务要求: 标准地面服务

此致,
运行团队`;

            default:
                return `请为以下航班提供服务:
${flightDetails}

此致,
运行团队`;
        }
    };

    // 获取收件人选项
    const getMockRecipients = (txType) => {
        // 根据不同事务类型返回不同的收件人选项
        const commonContacts = [
            { value: "operations@flymeta.com", label: "FlyMeta运行控制中心" },
            { value: "dispatch@flymeta.com", label: "FlyMeta签派部门" },
        ];

        switch (txType) {
            case "PERMIT":
                return [...commonContacts, { value: "permits@caac.gov.cn", label: "中国民航局许可处" }, { value: "bjatcc@caac.gov.cn", label: "北京空管局" }, { value: "permit@uas.aero", label: "UAS许可申请服务" }];
            case "FUEL":
                return [...commonContacts, { value: "fuel@cnaf.com", label: "中航油服务" }, { value: "aviation@bp.com", label: "BP航空燃油" }];
            case "SLOT":
                return [...commonContacts, { value: "slots@bcia.com.cn", label: "北京首都机场时刻协调" }, { value: "slots@sha.com.cn", label: "上海浦东机场时刻协调" }];
            case "HANDLING":
                return [...commonContacts, { value: "handling@bgh.com.cn", label: "北京地服公司" }, { value: "operations@sgs.com.cn", label: "上海地服公司" }];
            default:
                return commonContacts;
        }
    };

    const handleFileChange = ({ fileList }) => {
        setFileList(fileList);
    };

    const renderFormFields = () => {
        if (!formConfig.fields || formConfig.fields.length === 0) {
            return null;
        }

        return formConfig.fields.map((field) => {
            switch (field.type) {
                case "select":
                    return (
                        <Form.Item key={field.name} name={field.name} label={field.label} rules={[{ required: field.required, message: `请选择${field.label}` }]}>
                            <Select placeholder={`请选择${field.label}`}>
                                {field.options.map((opt) => (
                                    <Option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    );
                case "textarea":
                    return (
                        <Form.Item key={field.name} name={field.name} label={field.label} rules={[{ required: field.required, message: `请输入${field.label}` }]}>
                            <TextArea rows={4} placeholder={`请输入${field.label}`} />
                        </Form.Item>
                    );
                case "datepicker":
                    return (
                        <Form.Item key={field.name} name={field.name} label={field.label} rules={[{ required: field.required, message: `请选择${field.label}` }]}>
                            <DatePicker style={{ width: "100%" }} placeholder={`请选择${field.label}`} />
                        </Form.Item>
                    );
                case "daterange":
                    return (
                        <Form.Item key={field.name} name={field.name} label={field.label} rules={[{ required: field.required, message: `请选择${field.label}` }]}>
                            <DatePicker.RangePicker style={{ width: "100%" }} placeholder={[`开始日期`, `结束日期`]} />
                        </Form.Item>
                    );
                case "timepicker":
                    return (
                        <Form.Item key={field.name} name={field.name} label={field.label} rules={[{ required: field.required, message: `请选择${field.label}` }]}>
                            <DatePicker showTime format="YYYY-MM-DD HH:mm" placeholder="选择时间" style={{ width: "100%" }} />
                        </Form.Item>
                    );
                case "checkbox":
                    return (
                        <Form.Item key={field.name} name={field.name} label={field.label} rules={[{ required: field.required, message: `请选择${field.label}` }]}>
                            <Select mode="multiple" placeholder={`请选择${field.label}`} style={{ width: "100%" }} optionLabelProp="label">
                                {field.options.map((opt) => (
                                    <Option key={opt.value} value={opt.value} label={opt.label}>
                                        {opt.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    );
                default:
                    return (
                        <Form.Item key={field.name} name={field.name} label={field.label} rules={[{ required: field.required, message: `请输入${field.label}` }]}>
                            <Input placeholder={`请输入${field.label}`} />
                        </Form.Item>
                    );
            }
        });
    };

    return (
        <Card title={<Title level={4}>{formConfig.title}</Title>} bordered={false}>
            <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
                {formConfig.subTitle}
            </Text>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    status: "PENDING",
                    action_date: dayjs(),
                }}
            >
                {/* 航班信息展示区 */}
                <Card size="small" title="航班信息" style={{ marginBottom: 24 }} type="inner">
                    <Row gutter={16}>
                        <Col span={8}>
                            <Text type="secondary">航班号</Text>
                            <div>{flightInfo?.flight_number || "N/A"}</div>
                        </Col>
                        <Col span={8}>
                            <Text type="secondary">航班日期</Text>
                            <div>{flightInfo?.flight_date ? dayjs(flightInfo.flight_date).format("YYYY-MM-DD") : "N/A"}</div>
                        </Col>
                        <Col span={8}>
                            <Text type="secondary">航线</Text>
                            <div>
                                {flightInfo?.departure_airport || "N/A"}-{flightInfo?.arrival_airport || "N/A"}
                            </div>
                        </Col>
                    </Row>
                </Card>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="subject" label="主题" rules={[{ required: true, message: "请输入主题" }]}>
                            <Input placeholder="请输入主题" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="status" label="状态">
                            <Select disabled>
                                <Option value="PENDING">待处理</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="recipients" label="收件人" rules={[{ required: true, message: "请选择收件人" }]}>
                            <Select mode="multiple" placeholder="请选择收件人" optionLabelProp="label" optionFilterProp="label">
                                {recipientOptions.map((recipient) => (
                                    <Option key={recipient.value} value={recipient.value} label={recipient.label}>
                                        <Space>
                                            <span>{recipient.label}</span>
                                            <span style={{ color: "#ccc" }}>{recipient.value}</span>
                                        </Space>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="action_date" label="操作日期">
                            <DatePicker style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                </Row>

                {/* 渲染事务类型特定字段 */}
                {renderFormFields()}

                <Form.Item name="message" label="消息内容" rules={[{ required: true, message: "请输入消息内容" }]}>
                    <TextArea rows={6} placeholder="请输入消息内容" />
                </Form.Item>

                {formConfig.attachments && (
                    <Form.Item label="附件">
                        <Dragger multiple beforeUpload={() => false} onChange={handleFileChange} fileList={fileList}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                            <p className="ant-upload-hint">支持单个或批量上传</p>
                        </Dragger>
                    </Form.Item>
                )}

                <Divider />

                <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
                    <Space>
                        <Button onClick={onCancel}>取消</Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            发送
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
};

// 事务类型选项数据
const transactionTypes = [
    { value: "FUEL", label: "燃油申请" },
    { value: "PERMIT", label: "许可申请" },
    { value: "ACTIVATION", label: "航班激活" },
    { value: "MVT", label: "航班动态MVT" },
    { value: "CREW", label: "机组安排" },
    { value: "CATERING", label: "餐食服务" },
];

export default TransactionActionForm;
